<script setup lang="ts">
import { useAutoScroll } from "@/composition/useAutoScroll";
import { useColorResolver } from "@/composition/useColorResolver";
import { MediaCenterSubCategory } from "@/helpers/mediaCenter.type";
import { useAppStore } from "@/store/app";
import { CONSTANTS, IMAGE_LAYOUT } from "@/util/constants";
import { getCheckBoxStyles, scopeCustomStyle } from "@/util/default";
import {
  createBuilderScrollPreserver,
  preserveBuilderScrollPosition,
} from "@/util/focusNavigation";
import { getEffectiveBackgroundImageUrlForDevice } from "@/util/formStyleBackgroundImage";
import {
  addActiveTabLoopListener,
  fieldFullWidth,
  focusLabelAfterActiveChange,
  focusLabelFromUserEvent,
  getAllFontsImport,
  getElementDOMUniqueId,
  removeActiveTabLoopListener,
} from "@/util/methods";
import { storeToRefs } from "pinia";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import ContactAssociationSettingsModal from "../common/ContactAssociationSettingsModal.vue";
import CustomFieldsPreview from "../common/CustomFieldsPreview.vue";
import DefaultScreen from "../common/DefaultScreen.vue";
import HLImage from "../common/HLImage.vue";
import MixedCustomObjectErrorModal from "../common/MixedCustomObjectErrorModal.vue";
import StandardFieldPreview from "../common/StandardFieldPreview.vue";
import FormbuilderBranding from "./../common/BuilderBranding.vue";

const { t } = useI18n();
const store = useAppStore();
const { normalizeHex } = useColorResolver(store);

const builderCanvasBackgroundUrl = computed(() =>
  getEffectiveBackgroundImageUrlForDevice(store.formStyle, store.deviceMode)
);

const { elements } = storeToRefs(store);

const currentElement = ref({ label: "" });
const globalScriptCSSStyle = ref();
const globalScriptCSSLabelHolder = ref();
const globalScriptCSSPlaceHolder = ref();
const globalScriptCSSFieldBGColor = ref();
const globalScriptCSSFieldBorder = ref();
const globalScriptCSSFieldShadow = ref();
const globalScriptCSSFieldWidth = ref();
const globalScriptCSSFieldPadding = ref();
const globalScriptCSSFontHolder = ref();
const globalScriptCSSShortLabelHolder = ref();

// Auto-scroll during drag
const { handleDragStart, handleDragEnd } = useAutoScroll("form-builder");

const formWidth = computed(() => {
  return store.deviceMode === "mobile" ? 400 : store.formStyle.width;
});

const formStyle = computed(() => {
  const style = store.formStyle;
  // Use mobile padding if in mobile mode and it exists, otherwise fallback to desktop padding
  const padding =
    store.deviceMode === "mobile"
      ? style?.mobilePadding || style?.padding
      : style?.padding;

  return {
    padding: `${padding?.top || 0}px ${padding?.right || 0}px ${
      padding?.bottom || 0
    }px ${padding?.left || 0}px`,
    backgroundColor: `#${style?.background}`,
    border: `${style?.border?.border}px ${style?.border?.style} #${style?.border?.color}`,
    borderRadius: `${style?.border?.radius}px`,
    boxShadow: `${style?.shadow?.horizontal}px ${style?.shadow?.vertical}px ${style?.shadow?.blur}px ${style?.shadow?.spread}px #${style?.shadow?.color}`,
    width: `${formWidth.value}px`,
  };
});

const formOuterMargin = computed(() => {
  const style = store.formStyle;
  const margin =
    store.deviceMode === "mobile"
      ? style?.mobileMargin || style?.margin
      : style?.margin;
  const normalizeTop = (v) =>
    v === "auto" || v === undefined || v === null ? "20px" : `${v}px`;
  const normalizeBottom = (v) =>
    v === "auto" || v === undefined || v === null ? "80px" : `${v}px`;

  return {
    marginTop: normalizeTop(margin?.top),
    marginBottom: normalizeBottom(margin?.bottom),
  };
});

const currentLayout = computed(() => {
  return store.deviceMode === "mobile"
    ? store.builderConfig.mobileLayout || store.builderConfig.layout
    : store.builderConfig.layout;
});

const shouldRemoveExtraPadding = computed(() => {
  const padding =
    store.deviceMode === "mobile"
      ? store.formStyle.mobilePadding
      : store.formStyle.padding;
  return padding?.extraPaddingComputingProcessed === true;
});

const clickedElement = async (e, element, index) => {
  await preserveBuilderScrollPosition(async () => {
    e.stopPropagation();
    const alreadyActive = store.currentCustomSidebarPreview === index;
    store.currentCustomSidebarPreview = index;
    currentElement.value = element;
    await nextTick();
    if (!alreadyActive) {
      focusLabelFromUserEvent(e);
    }
  });
};

const preserveBuilderScroll = createBuilderScrollPreserver();

const fieldStyle = computed(() => {
  const fieldStyleStore = store.fieldStyle;
  const style = {
    padding: `${fieldStyleStore?.padding?.top}px ${fieldStyleStore?.padding?.right}px ${fieldStyleStore?.padding?.bottom}px ${fieldStyleStore?.padding?.left}px`,
    backgroundColor: "#" + fieldStyleStore?.bgColor,
    border: `${fieldStyleStore?.border?.border}px ${
      fieldStyleStore?.border?.type === "none"
        ? ""
        : fieldStyleStore?.border?.type
    } #${fieldStyleStore?.border?.color}`,
    borderRadius: fieldStyleStore?.border?.radius + "px",
    boxShadow: `${fieldStyleStore?.shadow?.horizontal}px ${
      fieldStyleStore?.shadow?.vertical
    }px ${fieldStyleStore?.shadow?.blur}px ${
      fieldStyleStore?.shadow?.spread
    }px #${normalizeHex(fieldStyleStore?.shadow?.color)}`,
    maxWidth: (fieldStyleStore?.width || 516) + "px",
    color: "#" + fieldStyleStore?.labelColor,
    mobileLabelColor: "#" + fieldStyleStore?.mobileLabelColor,
    font: `${fieldStyleStore.labelFontSize}px  ${fieldStyleStore.labelFontWeight} ${fieldStyleStore.labelFontFamily}`,
  };
  return style;
});

onMounted(() => {
  const globalScriptCSS = document.createElement("style");
  globalScriptCSS.type = "text/css";

  // Google and bunny font css script
  const globalScriptFontFamily = document.createElement("style");
  globalScriptFontFamily.type = "text/css";
  globalScriptFontFamily.appendChild(document.createTextNode(""));
  globalScriptCSSFontHolder.value = globalScriptFontFamily.childNodes[0];
  document.head.appendChild(globalScriptFontFamily);

  for (let i = 0; i < 9; i++) {
    globalScriptCSS.appendChild(document.createTextNode(""));
  }
  globalScriptCSSStyle.value = globalScriptCSS.childNodes[0];
  //For Label CSS Change
  globalScriptCSSLabelHolder.value = globalScriptCSS.childNodes[1];
  //For Placeholder CSS Change
  globalScriptCSSPlaceHolder.value = globalScriptCSS.childNodes[2];
  globalScriptCSSFieldBGColor.value = globalScriptCSS.childNodes[3];
  globalScriptCSSFieldBorder.value = globalScriptCSS.childNodes[4];
  globalScriptCSSFieldShadow.value = globalScriptCSS.childNodes[5];
  globalScriptCSSFieldWidth.value = globalScriptCSS.childNodes[6];
  globalScriptCSSFieldPadding.value = globalScriptCSS.childNodes[7];
  globalScriptCSSShortLabelHolder.value = globalScriptCSS.childNodes[8];

  document.head.appendChild(globalScriptCSS);
  globalScriptCSSFontHolder.value.textContent = getAllFontsImport(
    store.fontCollection,
    store.builderConfig.isGDPRCompliant
  );
  setGlobalStyleInHTMl();
  addActiveTabLoopListener();
});

onBeforeUnmount(() => {
  removeActiveTabLoopListener();
});

// Ensure focus also happens on programmatic selection changes
watch(
  () => store.currentCustomSidebarPreview,
  async () => {
    await nextTick();
    focusLabelAfterActiveChange();
  }
);

const setGlobalStyleInHTMl = () => {
  const fieldStyle = store.fieldStyle;
  const formStyle = store.formStyle;
  const isMobile = store.deviceMode === "mobile";

  const getMobileOrDesktop = (mobileVal, desktopVal) =>
    isMobile ? mobileVal || desktopVal : desktopVal;

  const labelColor = getMobileOrDesktop(
    fieldStyle.mobileLabelColor,
    fieldStyle.labelColor
  );
  const labelFontSize = getMobileOrDesktop(
    fieldStyle.mobileLabelFontSize,
    fieldStyle.labelFontSize
  );
  const labelFontWeight = getMobileOrDesktop(
    fieldStyle.mobileLabelFontWeight,
    fieldStyle.labelFontWeight
  );
  const labelFontFamily = getMobileOrDesktop(
    fieldStyle.mobileLabelFontFamily,
    fieldStyle.labelFontFamily
  );

  const placeholderColor = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderColor,
    fieldStyle.placeholderColor
  );
  const placeholderFontFamily = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderFontFamily,
    fieldStyle.placeholderFontFamily
  );
  const placeholderFontSize = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderFontSize,
    fieldStyle.placeholderFontSize
  );
  const placeholderFontWeight = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderFontWeight,
    fieldStyle.placeholderFontWeight
  );

  const shortLabelColor = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileColor,
    fieldStyle.shortLabel?.color
  );
  const shortLabelFontSize = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileFontSize,
    fieldStyle.shortLabel?.fontSize
  );
  const shortLabelFontWeight = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileFontWeight,
    fieldStyle.shortLabel?.fontWeight
  );
  const shortLabelFontFamily = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileFontFamily,
    fieldStyle.shortLabel?.fontFamily
  );
  const shadowColor = normalizeHex(fieldStyle.shadow?.color);

  globalScriptCSSLabelHolder.value.textContent = `#_builder-form label, #_builder-form .label-input {
      color: #${labelColor};
      font-size: ${labelFontSize}px;
      font-weight: ${labelFontWeight};
      font-family: '${labelFontFamily}';
      margin-bottom: 0.5rem;
      line-height: 1.8;
    }
    #_builder-form .tnc {
      font-family: '${labelFontFamily}';
    }
    #_builder-form pre {
      white-space: pre-wrap;
      word-break: break-word;
      max-width: 100%;
    }
    #_builder-form .product-amount {
      color: #${labelColor};
      font-size: ${parseFloat(labelFontSize) + 4}px;
      font-weight: ${parseFloat(labelFontWeight) + 200};
      font-family: ${labelFontFamily};
      line-height: 1.5rem;
    }
    #_builder-form .crossed-amount {
      color: #${labelColor};
      font-size: ${parseFloat(labelFontSize) + 2}px;
      font-weight: ${parseFloat(labelFontWeight) + 100};
      font-family: ${labelFontFamily};
      line-height: 1.5rem;
    }
    #_builder-form .product-label {
      color: #${labelColor};
      font-size: ${parseFloat(labelFontSize) + 2}px;
      font-weight: ${parseFloat(labelFontWeight) + 100};
      font-family: ${labelFontFamily};
      line-height: 1.575rem;
    }
    #_builder-form .setup-fee {
      color: #${labelColor};
      font-size: ${parseFloat(labelFontSize) - 2}px;
      font-weight: ${parseFloat(labelFontWeight)};
      font-family: ${labelFontFamily};
      line-height: 1.575rem;
    }
    #_builder-form .variant-tag {
      color: #${labelColor};
      font-size: ${parseFloat(labelFontSize) - 1}px;
      font-weight: ${parseFloat(labelFontWeight)};
      font-family: ${labelFontFamily};
      line-height: 1.5rem;
    }
    .bubble-label, .bubble-checkbox-label {
      background-color: #${fieldStyle.bgColor} !important;
      color: #${placeholderColor} !important;
      font-family: '${placeholderFontFamily}' !important;
      font-size: ${placeholderFontSize}px !important;
      font-weight: ${placeholderFontWeight} !important;
    }
    .bubble-container, .bubble-checkbox-container {
      border: ${fieldStyle.border.border}px ${fieldStyle.border.type} #${
    fieldStyle.border.color
  } !important;
      border-radius: 12px !important;
      /* Small horizontal gap between bubbles */
      margin-left: 2px !important;
      margin-right: 2px !important;
      padding: ${fieldStyle.padding.top}px ${fieldStyle.padding.right}px ${
    fieldStyle.padding.bottom
  }px ${fieldStyle.padding.left}px !important;
      box-shadow: ${fieldStyle.shadow.horizontal}px ${
    fieldStyle.shadow.vertical
  }px ${fieldStyle.shadow.blur}px ${fieldStyle.shadow.spread}px #${shadowColor};
      font-family: '${labelFontFamily}';
      font-size: ${labelFontSize}px;
      font-weight: ${labelFontWeight};
      background-clip: inherit !important;
    }
    `;

  globalScriptCSSPlaceHolder.value.textContent = `#_builder-form ::placeholder, #_builder-form .form-dropdown a, 
    #_builder-form .form-dropdown .dropdown-toggle , #_builder-form .currency-code, #_builder-form .form-placeholder, .input-icon { 
      color : #${placeholderColor}; 
      font-family:'${placeholderFontFamily}'; 
      font-size:${placeholderFontSize}px; 
      font-weight: ${placeholderFontWeight};
    }
    #_builder-form .bubble-label, #_builder-form .bubble-checkbox-label {
      font-family:'${placeholderFontFamily}' !important;
      font-size:${placeholderFontSize}px !important;
      font-weight:${placeholderFontWeight} !important;
    }`;
  globalScriptCSSFieldBGColor.value.textContent = `#_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown, #_builder-form .payment-tag, #_builder-form .quantity-container-counter, #_builder-form .other-input-control { background-color : #${fieldStyle.bgColor};}`;
  globalScriptCSSFieldBorder.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown, .form-input-border-style { border:" +
    fieldStyle.border.border +
    "px " +
    fieldStyle.border.type +
    " #" +
    fieldStyle.border.color +
    ";border-radius:" +
    fieldStyle.border.radius +
    "px ; }" +
    ` .line-form-style { border-bottom: ${fieldStyle.border.border}px ${fieldStyle.border.type} #${fieldStyle.border.color} !important; background-color: #${fieldStyle.bgColor} !important;}` +
    `.text-border-color-style { color : #${fieldStyle.border.color} } `;

  globalScriptCSSFieldPadding.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown , .input-form-padding-style { padding:" +
    fieldStyle.padding.top +
    "px " +
    fieldStyle.padding.right +
    "px " +
    fieldStyle.padding.bottom +
    "px " +
    fieldStyle.padding.left +
    "px}";
  globalScriptCSSFieldShadow.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown, #_builder-form .payment-tag, #_builder-form .quantity-container-counter, #_builder-form .other-input-control { box-shadow:" +
    fieldStyle.shadow.horizontal +
    "px " +
    fieldStyle.shadow.vertical +
    "px " +
    fieldStyle.shadow.blur +
    "px " +
    fieldStyle.shadow.spread +
    "px #" +
    shadowColor +
    " !important; }";
  globalScriptCSSFieldWidth.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown {max-width:" +
    fieldStyle.width +
    "px}";
  globalScriptCSSShortLabelHolder.value.textContent = `#_builder-form .short-label, #_builder-form .short-label::placeholder {
      color: #${shortLabelColor} !important;
      font-size: ${shortLabelFontSize}px !important;
      font-weight: ${shortLabelFontWeight};
      font-family: '${shortLabelFontFamily}';
  }`;
  const styleString = scopeCustomStyle(formStyle.customStyle);

  const checkboxStyle = getCheckBoxStyles(fieldStyle);
  globalScriptCSSStyle.value.textContent = styleString + checkboxStyle;
};

//Store Subscription to watch on store value change using patch
store.$subscribe((mutation, state) => {
  setGlobalStyleInHTMl();
});

watch([store.fontCollection, () => store.builderConfig.isGDPRCompliant], () => {
  globalScriptCSSFontHolder.value.textContent = getAllFontsImport(
    store.fontCollection,
    store.builderConfig.isGDPRCompliant
  );
});
const isActiveElement = (index) => {
  return store.currentCustomSidebarPreview === index;
};
const changeLog = async (e) => {
  await preserveBuilderScrollPosition(async () => {
    if (e.added) {
      await store.handleDropAndMove(e.added.element, e.added.newIndex);
      store.currentCustomSidebarPreview = e.added.newIndex;
      store.isAnyElementActive = true;
      await nextTick();
      focusLabelAfterActiveChange();
    } else if (e.moved) {
      await store.handleDropAndMove(
        e.moved.element,
        e.moved.newIndex,
        e.moved.oldIndex
      );
    }
  });
};

const clickedOutSideForm = () => {
  store.currentCustomSidebarPreview = undefined;
  store.isAnyElementActive = false;
};

const applyInlineStyle = (element) => {
  if (store.deviceMode === "mobile") {
    return {
      width: "100%",
      marginBottom: store.formStyle.fieldSpacing + "px",
    };
  }
  if (
    currentLayout.value === CONSTANTS.SINGLE_LINE &&
    !fieldFullWidth(element.type, true)
  ) {
    return {
      width: element.fieldWidthPercentage + "%",
      marginBottom: store.formStyle.fieldSpacing + "px",
    };
  }
  if (
    currentLayout.value !== CONSTANTS.SINGLE_LINE &&
    !fieldFullWidth(element.type, false)
  ) {
    return {
      width: element.fieldWidthPercentage + "%",
      marginBottom: store.formStyle.fieldSpacing + "px",
    };
  }
  return { marginBottom: store.formStyle.fieldSpacing + "px" };
};

const updateLabel = (newLabel: string, index: number) => {
  store.elements[index].label = newLabel;
  store.anyUnsavedChanges = true;
};

const updatePlaceholder = (newPlaceholder: string, index: number) => {
  store.elements[index].placeholder = newPlaceholder;
  store.anyUnsavedChanges = true;
};

const updateShortLabel = (newShortLabel: string, index: number) => {
  store.elements[index].shortLabel = newShortLabel;
  store.anyUnsavedChanges = true;
};

const updateLowestRating = (lowestRating: string, index: number) => {
  store.elements[index].lowestRating = lowestRating;
  store.anyUnsavedChanges = true;
};

const updateHighestRating = (highestRating: string, index: number) => {
  store.elements[index].highestRating = highestRating;
  store.anyUnsavedChanges = true;
};

/**
 * Handle contact association settings save
 */
const saveContactAssociationSettings = (settings) => {
  store.contactAssociationSettings = settings;
  store.anyUnsavedChanges = true;
  store.showContactAssociationModal = false;
};

/**
 * Handle contact association settings cancel
 */
const cancelContactAssociationSettings = () => {
  store.showContactAssociationModal = false;
};
// Image layout computed properties
const currentImageLayout = computed(() => {
  if (!store.builderConfig.showImage) {
    return null;
  }
  return store.deviceMode === "mobile"
    ? store.builderConfig.mobileImageLayout || IMAGE_LAYOUT.HEADER
    : store.builderConfig.desktopImageLayout || IMAGE_LAYOUT.TOP_FIXED;
});

const hasImage = computed(() => {
  const hasMobileImage =
    (store.formAction?.mobileHeaderImageSrc || "").trim().length > 0;
  const hasDesktopImage =
    (store.formAction?.headerImageSrc || "").trim().length > 0;
  const mobileHeaderImageDeleted = Boolean(
    store.formAction?.mobileHeaderImageSrcDeleted
  );

  if (store.deviceMode === "mobile") {
    return hasMobileImage || (!mobileHeaderImageDeleted && hasDesktopImage);
  }

  return hasDesktopImage;
});

const shouldShowImage = computed(() => {
  return store.builderConfig.showImage && hasImage.value;
});

// Auto-enable showImage if image exists (backward compatibility)
watch(
  hasImage,
  (newHasImage) => {
    if (newHasImage && !store.builderConfig.showImage) {
      store.builderConfig.showImage = true;
    }
  },
  { immediate: true }
);

const imageUrl = computed(() => {
  if (store.deviceMode === "mobile") {
    const mobileUrl = store.formAction?.mobileHeaderImageSrc || "";
    if (mobileUrl.trim().length > 0) {
      return mobileUrl;
    }
    if (store.formAction?.mobileHeaderImageSrcDeleted) {
      return "";
    }
    return store.formAction?.headerImageSrc;
  }

  return store.formAction?.headerImageSrc;
});

// Image focus point for leftFixed/rightFixed layouts (desktop only)
const imageFocusPoint = computed(() => {
  const defaultFocusPoint = { x: 50, y: 50 };
  return (store.builderConfig as any).imageFocusPoint || defaultFocusPoint;
});

// Background position string based on focus point
const imageBackgroundPosition = computed(() => {
  return `${imageFocusPoint.value.x}% ${imageFocusPoint.value.y}%`;
});

// Dynamic CSS class for image layout
const imageLayoutClass = computed(() => {
  if (!shouldShowImage.value || !currentImageLayout.value) {
    return "";
  }

  // Convert camelCase to kebab-case for CSS classes
  const layoutClass = currentImageLayout.value
    .replace(/([A-Z])/g, "-$1")
    .toLowerCase();

  return `image-layout-${layoutClass}`;
});

// Enhanced form style - padding adjusted based on layout
const enhancedFormStyle = computed(() => {
  const baseStyle = formStyle.value;
  const radius = store.formStyle.border.radius || 0;

  // Add background image for background layout
  if (
    currentImageLayout.value === IMAGE_LAYOUT.BACKGROUND &&
    shouldShowImage.value
  ) {
    return {
      ...baseStyle,
      backgroundImage: `url(${imageUrl.value})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    };
  }

  // Adjust border radius based on image layout
  let borderRadius = `${radius}px`;

  if (shouldShowImage.value) {
    if (
      currentImageLayout.value === IMAGE_LAYOUT.TOP_FIXED ||
      currentImageLayout.value === IMAGE_LAYOUT.HEADER
    ) {
      // Image is on top, so form gets bottom corners rounded
      borderRadius = `0 0 ${radius}px ${radius}px`;
    } else if (currentImageLayout.value === IMAGE_LAYOUT.LEFT_FIXED) {
      // Image is on left, so form gets right corners rounded
      borderRadius = `${radius}px`;
    } else if (currentImageLayout.value === IMAGE_LAYOUT.RIGHT_FIXED) {
      // Image is on right, so form gets left corners rounded
      borderRadius = `${radius}px`;
    }
  }

  // For leftFixed/rightFixed layouts, remove left/right padding from the container
  // as it will be applied to form-side only
  let adjustedPadding = baseStyle.padding;
  if (
    shouldShowImage.value &&
    store.deviceMode !== "mobile" &&
    (currentImageLayout.value === IMAGE_LAYOUT.LEFT_FIXED ||
      currentImageLayout.value === IMAGE_LAYOUT.RIGHT_FIXED)
  ) {
    const style = store.formStyle;
    const padding =
      store.deviceMode === "mobile"
        ? style?.mobilePadding || style?.padding
        : style?.padding;

    if (currentImageLayout.value === IMAGE_LAYOUT.LEFT_FIXED) {
      // Remove left padding from container, keep others
      adjustedPadding = `${padding?.top || 0}px ${padding?.right || 0}px ${
        padding?.bottom || 0
      }px 0px`;
    } else if (currentImageLayout.value === IMAGE_LAYOUT.RIGHT_FIXED) {
      // Remove right padding from container, keep others
      adjustedPadding = `${padding?.top || 0}px 0px ${padding?.bottom || 0}px ${
        padding?.left || 0
      }px`;
    }
  }

  return {
    ...baseStyle,
    padding: adjustedPadding,
    borderRadius,
  };
});

// Form side style - width, spacing, and padding for fixed layouts
// The spacing is cut from form side to keep image side at exact 40%
const formSideStyle = computed(() => {
  // In mobile mode, always use 100% width. In desktop, use layout-specific widths.
  if (store.deviceMode === "mobile") {
    return { width: "100%" };
  }

  if (
    currentImageLayout.value === IMAGE_LAYOUT.BACKGROUND ||
    currentImageLayout.value === IMAGE_LAYOUT.TOP_FIXED ||
    !currentImageLayout.value
  ) {
    return { width: "100%" };
  }

  const style = store.formStyle;
  const padding =
    store.deviceMode === "mobile"
      ? style?.mobilePadding || style?.padding
      : style?.padding;

  // For leftFixed layout, apply left padding to form content (no gap spacing)
  if (
    shouldShowImage.value &&
    currentImageLayout.value === IMAGE_LAYOUT.LEFT_FIXED
  ) {
    return {
      width: "60%",
      paddingLeft: `${padding?.left || 0}px`,
    };
  }

  // For rightFixed layout, apply right padding to form content (no gap spacing)
  if (
    shouldShowImage.value &&
    currentImageLayout.value === IMAGE_LAYOUT.RIGHT_FIXED
  ) {
    return {
      width: "60%",
      paddingRight: `${padding?.right || 0}px`,
    };
  }

  // For side layouts (leftCenter, rightCenter),
  // cut the spacing from form side so image side remains at 40%
  const spacing = store.builderConfig.imageFieldSpacing ?? 24;
  return { width: `calc(60% - ${spacing}px)` };
});

// Image and form gap spacing (only for leftCenter and rightCenter layouts)
const imageFormGap = computed(() => {
  const shouldShowGap =
    shouldShowImage.value &&
    (
      [IMAGE_LAYOUT.LEFT_CENTER, IMAGE_LAYOUT.RIGHT_CENTER] as string[]
    ).includes(currentImageLayout.value);

  if (!shouldShowGap) {
    return "0px";
  }

  return `${store.builderConfig.imageFieldSpacing ?? 24}px`;
});

// Image side width - always 40% for side layouts
const imageSideWidth = computed(() => {
  const shouldUseFixedWidth =
    shouldShowImage.value &&
    (
      [
        IMAGE_LAYOUT.LEFT_CENTER,
        IMAGE_LAYOUT.RIGHT_CENTER,
        IMAGE_LAYOUT.LEFT_FIXED,
        IMAGE_LAYOUT.RIGHT_FIXED,
      ] as string[]
    ).includes(currentImageLayout.value) &&
    store.deviceMode !== "mobile";

  if (!shouldUseFixedWidth) {
    return "40%";
  }

  return "40%";
});

// Top image border radius (for topFixed/header layouts)
const topImageBorderRadius = computed(() => {
  const radius = store.formStyle.border.radius || 0;
  if (
    currentImageLayout.value === IMAGE_LAYOUT.TOP_FIXED ||
    currentImageLayout.value === IMAGE_LAYOUT.HEADER
  ) {
    return `${radius}px ${radius}px 0 0`;
  }
  return "0";
});

// Side image border radius (for leftFixed/rightFixed layouts)
const sideImageBorderRadius = computed(() => {
  const radius = store.formStyle.border.radius || 0;
  if (currentImageLayout.value === IMAGE_LAYOUT.LEFT_FIXED) {
    // Image on left gets left corners rounded
    return `${radius}px 0 0 ${radius}px`;
  } else if (currentImageLayout.value === IMAGE_LAYOUT.RIGHT_FIXED) {
    // Image on right gets right corners rounded
    return `0 ${radius}px ${radius}px 0`;
  }
  return "0";
});

// Image side negative margins to counteract form padding (for leftFixed/rightFixed)
const imageSideMargins = computed(() => {
  const shouldApplyMargins =
    shouldShowImage.value &&
    ([IMAGE_LAYOUT.LEFT_FIXED, IMAGE_LAYOUT.RIGHT_FIXED] as string[]).includes(
      currentImageLayout.value
    ) &&
    store.deviceMode !== "mobile";

  if (!shouldApplyMargins) {
    return {};
  }

  const style = store.formStyle;
  const padding =
    store.deviceMode === "mobile"
      ? style?.mobilePadding || style?.padding
      : style?.padding;

  const topPadding = padding?.top || 0;
  const bottomPadding = padding?.bottom || 0;

  return {
    marginTop: `-${topPadding}px`,
    marginBottom: `-${bottomPadding}px`,
  };
});
</script>

<template>
  <section
    id="form-builder"
    class="hl_wrapper--inner form-builder"
    :style="{
      backgroundImage: `url(${builderCanvasBackgroundUrl})`,
      backgroundSize: 'cover',
    }"
    @click.prevent="clickedOutSideForm"
    @mousedown.capture="preserveBuilderScroll"
    @focusin.capture="preserveBuilderScroll"
  >
    <section class="hl_form-builder--main">
      <form
        id="_builder-form"
        name="builder-form"
        class="builder-preview"
        :style="formOuterMargin"
      >
        <div
          v-if="
            hasImage &&
            (!shouldShowImage ||
              currentImageLayout === IMAGE_LAYOUT.TOP_FIXED ||
              currentImageLayout === IMAGE_LAYOUT.HEADER)
          "
        >
          <div
            class="ghl-header-banner w-full max-w-full"
            :style="{
              maxWidth: formWidth + 'px',
            }"
          >
            <HLImage
              :url="imageUrl"
              :width="store.formStyle.width"
              :alt="t('common.bannerAlt')"
              :style="{ borderRadius: topImageBorderRadius }"
            ></HLImage>
          </div>
        </div>
        <div
          id="the-form-builder"
          class="form-element form-builder--wrap"
          :style="enhancedFormStyle"
        >
          <!-- Image Layout Container -->
          <div
            v-if="shouldShowImage"
            :class="['flex items-center', imageLayoutClass]"
            :style="{ gap: imageFormGap }"
          >
            <div
              v-if="
                store.deviceMode !== 'mobile' &&
                currentImageLayout !== IMAGE_LAYOUT.TOP_FIXED &&
                currentImageLayout !== IMAGE_LAYOUT.BACKGROUND
              "
              class="image-side"
              :style="
                imageLayoutClass === 'image-layout-left-fixed' ||
                imageLayoutClass === 'image-layout-right-fixed'
                  ? {
                      width: imageSideWidth,
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: imageBackgroundPosition,
                      backgroundRepeat: 'no-repeat',
                      height: '-webkit-fill-available',
                      borderRadius: sideImageBorderRadius,
                      ...imageSideMargins,
                    }
                  : {
                      width: imageSideWidth,
                    }
              "
            >
              <HLImage
                v-if="
                  !(
                    imageLayoutClass === 'image-layout-left-fixed' ||
                    imageLayoutClass === 'image-layout-right-fixed'
                  ) &&
                  currentImageLayout !== IMAGE_LAYOUT.TOP_FIXED &&
                  currentImageLayout !== IMAGE_LAYOUT.BACKGROUND
                "
                :url="imageUrl"
                :alt="t('common.bannerAlt')"
                class="h-auto w-full"
                :style="{
                  borderRadius:
                    currentImageLayout === IMAGE_LAYOUT.LEFT_CENTER ||
                    currentImageLayout === IMAGE_LAYOUT.RIGHT_CENTER
                      ? `${store.builderConfig.imageCornerRadius || 0}px`
                      : '0px',
                }"
              />
            </div>
            <div class="form-side" :style="formSideStyle">
              <div
                :class="[
                  currentLayout !== CONSTANTS.TWO_COLUMN
                    ? `drop form-builder-drop ${
                        shouldRemoveExtraPadding ? '' : 'builder-padding-remove'
                      }`
                    : 'drop form-builder-drop builder-inline',
                ]"
              >
                <VueDraggableNext
                  id="form-builder-container"
                  :model-value="store.elements"
                  :class="
                    [CONSTANTS.TWO_COLUMN, CONSTANTS.SINGLE_LINE].includes(
                      currentLayout
                    )
                      ? 'smooth-dnd-container vertical row'
                      : 'smooth-dnd-container vertical'
                  "
                  group="elements"
                  :sort="true"
                  :animation="300"
                  handle=".field-draggable-area"
                  ghost-class="field-drag-ghost"
                  @start="handleDragStart"
                  @end="handleDragEnd"
                  @change="changeLog"
                >
                  <div v-if="!elements.length">
                    <DefaultScreen :add-slide-text="t('survey.addSlideText')" />
                  </div>
                  <div
                    v-for="(element, index) in elements"
                    v-else
                    :key="getElementDOMUniqueId('form-element', element, index)"
                    :style="applyInlineStyle(element)"
                    class="smooth-dnd-draggable-wrapper element-box menu-field-wrap form-field-wrapper"
                    @click="(e) => clickedElement(e, element, index)"
                  >
                    <div :class="{ active: isActiveElement(index) }">
                      <StandardFieldPreview
                        v-if="!(element.id !== undefined || element?.custom)"
                        :key="
                          getElementDOMUniqueId('std-field', element, index)
                        "
                        :element="element"
                        :index="index"
                        :current-element="currentElement"
                        :field-style="fieldStyle"
                        :form-label-visible="
                          store.builderConfig.formLabelVisible
                        "
                        @update:label="(label) => updateLabel(label, index)"
                        @update:placeholder="
                          (placeholder) => updatePlaceholder(placeholder, index)
                        "
                        @update:short-label="
                          (shortLabel) => updateShortLabel(shortLabel, index)
                        "
                        :media-center-sub-category="MediaCenterSubCategory.FORM"
                      ></StandardFieldPreview>
                      <CustomFieldsPreview
                        v-if="element.id !== undefined || element?.custom"
                        :key="
                          getElementDOMUniqueId('custom-field', element, index)
                        "
                        :element="element"
                        :index="index"
                        :current-element="currentElement"
                        :field-style="fieldStyle"
                        :form-label-visible="
                          store.builderConfig.formLabelVisible
                        "
                        :is-form="true"
                        @update:label="(label) => updateLabel(label, index)"
                        @update:placeholder="
                          (placeholder) => updatePlaceholder(placeholder, index)
                        "
                        @update:short-label="
                          (shortLabel) => updateShortLabel(shortLabel, index)
                        "
                        @update:lowest-rating="
                          (lowestRating) =>
                            updateLowestRating(lowestRating, index)
                        "
                        @update:highest-rating="
                          (highestRating) =>
                            updateHighestRating(highestRating, index)
                        "
                      ></CustomFieldsPreview>
                    </div>
                  </div>
                </VueDraggableNext>
              </div>
              <!-- Branding inside form-side -->
              <FormbuilderBranding
                :is-branding-active="store.formStyle.acBranding"
                :company="store.company"
              />
            </div>
          </div>

          <!-- Fallback for when no image layout is active -->
          <div v-else>
            <div
              :class="
                currentLayout !== CONSTANTS.TWO_COLUMN
                  ? `drop form-builder-drop ${
                      shouldRemoveExtraPadding ? '' : 'builder-padding-remove'
                    }`
                  : 'drop form-builder-drop builder-inline'
              "
            >
              <VueDraggableNext
                id="form-builder-container"
                :model-value="store.elements"
                :class="
                  [CONSTANTS.TWO_COLUMN, CONSTANTS.SINGLE_LINE].includes(
                    currentLayout
                  )
                    ? 'smooth-dnd-container vertical row'
                    : 'smooth-dnd-container vertical'
                "
                group="elements"
                :sort="true"
                :animation="300"
                handle=".field-draggable-area"
                ghost-class="field-drag-ghost"
                @start="handleDragStart"
                @end="handleDragEnd"
                @change="changeLog"
              >
                <div v-if="!elements.length">
                  <DefaultScreen :add-slide-text="t('survey.addSlideText')" />
                </div>
                <div
                  v-for="(element, index) in elements"
                  :key="getElementDOMUniqueId('form-element', element, index)"
                  :style="applyInlineStyle(element)"
                  class="smooth-dnd-draggable-wrapper element-box menu-field-wrap form-field-wrapper"
                  @click="(e) => clickedElement(e, element, index)"
                >
                  <div :class="{ active: isActiveElement(index) }">
                    <StandardFieldPreview
                      v-if="!(element.id !== undefined || element?.custom)"
                      :key="getElementDOMUniqueId('std-field', element, index)"
                      :element="element"
                      :index="index"
                      :current-element="currentElement"
                      :field-style="fieldStyle"
                      :form-label-visible="store.builderConfig.formLabelVisible"
                      @update:label="(label) => updateLabel(label, index)"
                      @update:placeholder="
                        (placeholder) => updatePlaceholder(placeholder, index)
                      "
                      @update:short-label="
                        (shortLabel) => updateShortLabel(shortLabel, index)
                      "
                      :media-center-sub-category="MediaCenterSubCategory.FORM"
                    ></StandardFieldPreview>
                    <CustomFieldsPreview
                      v-if="element.id !== undefined || element?.custom"
                      :key="
                        getElementDOMUniqueId('custom-field', element, index)
                      "
                      :element="element"
                      :index="index"
                      :current-element="currentElement"
                      :field-style="fieldStyle"
                      :form-label-visible="store.builderConfig.formLabelVisible"
                      :is-form="true"
                      @update:label="(label) => updateLabel(label, index)"
                      @update:placeholder="
                        (placeholder) => updatePlaceholder(placeholder, index)
                      "
                      @update:short-label="
                        (shortLabel) => updateShortLabel(shortLabel, index)
                      "
                      @update:lowest-rating="
                        (lowestRating) =>
                          updateLowestRating(lowestRating, index)
                      "
                      @update:highest-rating="
                        (highestRating) =>
                          updateHighestRating(highestRating, index)
                      "
                    ></CustomFieldsPreview>
                  </div>
                </div>
              </VueDraggableNext>
            </div>
            <!-- Branding in fallback -->
            <FormbuilderBranding
              :is-branding-active="store.formStyle.acBranding"
              :company="store.company"
            />
          </div>
        </div>
      </form>
    </section>
    <ContactAssociationSettingsModal
      :show="store.showContactAssociationModal"
      :object-model="store.currentCustomObjectModel"
      @update:success="saveContactAssociationSettings"
      @update:cancel="cancelContactAssociationSettings"
    />
    <MixedCustomObjectErrorModal
      :show="store.showMixedCustomObjectErrorModal"
      :existing-object-name="
        store.mixedCustomObjectErrorInfo.existingObjectName
      "
      :attempted-object-name="
        store.mixedCustomObjectErrorInfo.attemptedObjectName
      "
      @update:close="store.showMixedCustomObjectErrorModal = false"
    />
  </section>
</template>

<style scoped>
.builder-preview {
  margin: 0 auto;
  width: 100%;
}

#form-builder {
  background-color: #f3f4f6;
  margin-bottom: 0px;
  overflow: auto;
  height: calc(100vh - 52px);
}

.builder-item {
  cursor: pointer;
  width: 100%;
  background: grey;
  margin: 0.5rem 0;
  min-height: 3rem;
}

#_builder-form .active {
  background: transparent;
  cursor: move;
}

.form-builder--item {
  margin-bottom: 15px;
  border-radius: 4px;
  border: 1px solid transparent;
  -webkit-transition: border 0.2s ease-in-out;
  -o-transition: border 0.2s ease-in-out;
  transition: border 0.2s ease-in-out;
  position: relative;
}

.builder-inline
  .smooth-dnd-draggable-wrapper.col-md-6
  .f-even
  .form-builder--item {
  padding-left: 4px !important;
}

.builder-inline
  .smooth-dnd-draggable-wrapper.col-md-6
  .f-odd
  .form-builder--item {
  padding-left: 10px;
  padding-right: 4px;
}

.builder-inline .smooth-dnd-draggable-wrapper.col-12 .form-builder--item {
  padding-left: 10px;
  padding-right: 4px;
}

.form-builder--item h1,
.form-builder--item h2,
.form-builder--item h3,
.form-builder--item h4,
.form-builder--item h5,
.form-builder--item h6,
.form-builder--item p {
  margin-bottom: 0;
}

.form-builder--item label {
  font-size: 0.875rem;
  color: #2a3135;
}

.button-element {
  min-width: 85px;
}

.menu-item {
  position: relative;
}

.menu-field-wrap {
  position: relative;
}

#the-form-builder .menu-field-wrap .close-icon {
  position: absolute;
  right: 10px;
  top: 5px;
  opacity: 1;
  transition: all 0.5s ease-in;
  font-size: 10px;
  background: #f5f5f5;
  padding: 5px 7px;
  border-radius: 50%;
  height: 20px;
  cursor: pointer;
}

#the-form-builder {
  margin: 0 auto;
  margin-bottom: 70px;
}

.form-builder-drop {
  min-height: 400px;
  overflow: visible;
  position: relative;
  display: flex;
  flex-direction: column;
}
.builder-padding-remove {
  padding: 30px 0;
}

.add-dropzone-builder {
  background: transparent;
  width: 100%;
  height: 86px;
  border-width: 1;
  border-style: solid;
  border-color: #e5e7eb;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #6c757d !important;
}

.add-dropzone-builder p {
  margin-left: 5px !important;
}

#form-builder-container {
  flex: 1;
  min-height: 100%;
}

.ghl-header-banner {
  margin: 0 auto;
}

:deep(.form-control) {
  font-size: 0.875rem;
}

.element-box {
  display: flex;
  flex-flow: column;
  height: 100%;
}

.smooth-dnd-draggable-wrapper {
  cursor: move;
}

.field-drag-ghost {
  opacity: 1 !important;
  background-color: #f2f4f7 !important;
  border-radius: 4px;
  min-height: 60px;
  position: relative;
}

.field-drag-ghost * {
  visibility: hidden !important;
}
/* Image Layout Styles */
.image-layout-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  justify-content: center;
  align-items: center;
}

.image-layout-top-fixed,
.image-layout-header {
  flex-direction: column;
  width: 100%;
}

.image-layout-left-center,
.image-layout-right-center {
  flex-direction: row;
  align-items: center;
  width: 100%;
  display: flex;
}

.image-layout-right-center {
  flex-direction: row-reverse;
}

.image-layout-left-center .image-side {
  overflow: hidden;
}

.image-layout-right-center .image-side {
  overflow: hidden;
}

.image-layout-left-fixed,
.image-layout-right-fixed {
  flex-direction: row;
  width: 100%;
  display: flex;
  min-height: 500px; /* Ensure height for fixed layouts */
}

.image-layout-right-fixed {
  flex-direction: row-reverse;
}

.image-side {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.form-side {
  min-height: 200px;
  overflow: visible;
  flex-shrink: 0;
}

/* Background and topFixed layouts - form takes full width */
.image-layout-background .form-side,
.image-layout-top-fixed .form-side {
  width: 100% !important;
}

/* For layouts with moved padding, ensure form-side handles padding properly */
/* Also vertically center form content for left/right image layouts */
.image-layout-left-center .form-side,
.image-layout-right-center .form-side,
.image-layout-left-fixed .form-side,
.image-layout-right-fixed .form-side {
  display: flex;
  flex-direction: column;
  justify-content: center; /* Stretch to fill parent height so justify-content works */
  box-sizing: border-box;
}

/* Remove min-height constraint on form-builder-drop for side image layouts to allow proper centering */
.image-layout-left-center .form-builder-drop,
.image-layout-right-center .form-builder-drop,
.image-layout-left-fixed .form-builder-drop,
.image-layout-right-fixed .form-builder-drop {
  min-height: auto;
}

/* Background layout specific styles */
#_builder-form[style*="background-image"] {
  position: relative;
  z-index: 1;
}

@media screen and (max-width: 650px) {
  .form-builder--item {
    margin-bottom: 10px;
  }

  .image-layout-left-center,
  .image-layout-right-center,
  .image-layout-left-fixed,
  .image-layout-right-fixed {
    flex-direction: column;
  }

  .image-side,
  .form-side {
    width: 100% !important;
  }

  .image-layout-left-fixed,
  .image-layout-right-fixed {
    min-height: auto;
  }
}
</style>
